import { useEffect, useState } from "react";
import AdminLayout from "@/pages/admin/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";

const PRODUCT_OPTIONS = [
  { slug: "purified-water", label: "Private Label Purified Water" },
  { slug: "alkaline-water", label: "Private Label Alkaline Water" },
  { slug: "spring-water", label: "Private Label Spring Water" },
  { slug: "sparkling-water", label: "Private Label Sparkling Water" },
];

const emptyForm = {
  slug: "",
  label: "",
  oz: 0,
  ml: 0,
  status: "active",
  public_visible: true,
  display_order: 100,
  compatible_product_slugs: [],
};

const StatusPill = ({ status, publicVisible }) => {
  const cls = status === "active" && publicVisible
    ? "bg-[var(--ft-gold)] text-[var(--ft-black)]"
    : status === "active"
    ? "bg-white text-[var(--ft-charcoal)] border border-[var(--ft-border)]"
    : "bg-[var(--ft-charcoal)] text-white/80";
  const label = status === "inactive" ? "Inactive" : publicVisible ? "Active · Public" : "Active · Hidden";
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.625rem] font-bold tracking-[0.18em] uppercase ${cls}`}>
      {label}
    </span>
  );
};

const AdminBottleSizes = () => {
  const [sizes, setSizes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => {
    api.get("/admin/bottle-sizes").then((r) => setSizes(r.data.bottle_sizes || [])).catch(() => toast.error("Failed to load bottle sizes"));
  };

  useEffect(() => { load(); }, []);

  const startEdit = (s) => {
    setEditing(s);
    setForm({
      slug: s.slug,
      label: s.label || "",
      oz: s.oz || 0,
      ml: s.ml || 0,
      status: s.status || "active",
      public_visible: !!s.public_visible,
      display_order: s.display_order || 100,
      compatible_product_slugs: s.compatible_product_slugs || [],
    });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.slug;
        await api.patch(`/admin/bottle-sizes/${editing.id}`, payload);
        toast.success("Bottle size updated");
      } else {
        await api.post("/admin/bottle-sizes", form);
        toast.success("Bottle size created");
      }
      setEditing(null);
      setShowNew(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const removeSize = async (s) => {
    if (!window.confirm(`Delete bottle size "${s.label}"?`)) return;
    try {
      await api.delete(`/admin/bottle-sizes/${s.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const reorder = async (s, delta) => {
    try {
      await api.patch(`/admin/bottle-sizes/${s.id}`, { display_order: (s.display_order || 100) + delta });
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const togglePublic = async (s) => {
    try {
      await api.patch(`/admin/bottle-sizes/${s.id}`, { public_visible: !s.public_visible });
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleStatus = async (s) => {
    try {
      const next = s.status === "active" ? "inactive" : "active";
      await api.patch(`/admin/bottle-sizes/${s.id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const toggleProduct = (slug) => {
    setForm((f) => ({
      ...f,
      compatible_product_slugs: f.compatible_product_slugs.includes(slug)
        ? f.compatible_product_slugs.filter((s) => s !== slug)
        : [...f.compatible_product_slugs, slug],
    }));
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-start mb-8 flex-wrap gap-4">
        <div>
          <div className="ft-eyebrow mb-2">Bottle Sizes</div>
          <h1 className="text-3xl font-bold tracking-tight text-[var(--ft-black)]">Bottle Sizes Manager</h1>
          <p className="text-[var(--ft-muted)] mt-2 max-w-2xl">
            Version 1.0 offers two sizes across all active water products. Add future sizes,
            toggle visibility, and set product compatibility here — no code changes required.
          </p>
        </div>
        <button onClick={() => { setForm(emptyForm); setShowNew(true); }} className="ft-btn-primary" data-testid="new-bottle-size-btn">
          <Plus size={16} /> New Bottle Size
        </button>
      </div>

      <div className="ft-card p-0 overflow-hidden">
        <table className="ft-table" data-testid="admin-bottle-sizes-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Order</th>
              <th>Label</th>
              <th>Volume</th>
              <th>Products</th>
              <th>Status</th>
              <th style={{ width: 240 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sizes.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[var(--ft-muted)] py-10">No bottle sizes yet.</td></tr>
            )}
            {sizes.map((s) => (
              <tr key={s.id} data-testid={`bottle-size-row-${s.slug}`}>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => reorder(s, -1)} className="p-1 text-[var(--ft-muted)] hover:text-[var(--ft-gold)]" data-testid={`bs-reorder-up-${s.slug}`}><ArrowUp size={14} /></button>
                    <span className="text-xs font-mono text-[var(--ft-muted)] w-6 text-center">{s.display_order}</span>
                    <button onClick={() => reorder(s, 1)} className="p-1 text-[var(--ft-muted)] hover:text-[var(--ft-gold)]" data-testid={`bs-reorder-down-${s.slug}`}><ArrowDown size={14} /></button>
                  </div>
                </td>
                <td>
                  <div className="font-semibold text-[var(--ft-black)]">{s.label}</div>
                  <div className="text-xs font-mono text-[var(--ft-muted)]">{s.slug}</div>
                </td>
                <td className="text-sm text-[var(--ft-charcoal)]">
                  <div>{s.oz} oz</div>
                  <div className="text-xs text-[var(--ft-muted)]">{s.ml >= 1000 ? `${(s.ml / 1000).toFixed(s.ml % 1000 === 0 ? 0 : 2)} L` : `${s.ml} mL`}</div>
                </td>
                <td className="text-xs text-[var(--ft-charcoal)]">
                  {(s.compatible_product_slugs || []).length === 0 ? (
                    <span className="italic text-[var(--ft-muted)]">All</span>
                  ) : (
                    <span>{s.compatible_product_slugs.length} product{s.compatible_product_slugs.length === 1 ? "" : "s"}</span>
                  )}
                </td>
                <td><StatusPill status={s.status} publicVisible={s.public_visible} /></td>
                <td>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => startEdit(s)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`bs-edit-${s.slug}`}>
                      Edit
                    </button>
                    <button onClick={() => togglePublic(s)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)] inline-flex items-center gap-1.5" data-testid={`bs-toggle-public-${s.slug}`}>
                      {s.public_visible ? <><EyeOff size={12} /> Hide</> : <><Eye size={12} /> Show</>}
                    </button>
                    <button onClick={() => toggleStatus(s)} className="text-xs px-3 py-1.5 rounded-full border border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]" data-testid={`bs-toggle-status-${s.slug}`}>
                      {s.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => removeSize(s)} className="p-1.5 rounded-full text-[var(--ft-muted)] hover:text-[var(--ft-error)]" data-testid={`bs-delete-${s.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(editing || showNew) && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setEditing(null); setShowNew(false); }}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="ft-eyebrow mb-1">{editing ? "Edit" : "New"}</div>
                <h2 className="text-2xl font-bold tracking-tight">{editing ? editing.label : "New Bottle Size"}</h2>
              </div>
              <button onClick={() => { setEditing(null); setShowNew(false); }} className="text-[var(--ft-muted)] hover:text-[var(--ft-black)]"><X size={22} /></button>
            </div>

            <form onSubmit={submitForm} className="space-y-5" data-testid="bottle-size-form">
              <div>
                <label className="ft-label">Display Label*</label>
                <input required className="ft-input" placeholder="16.9 oz (500 mL)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="bs-label" />
              </div>

              <div>
                <label className="ft-label">Slug*</label>
                <input required disabled={!!editing} className="ft-input font-mono text-sm disabled:opacity-70" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} data-testid="bs-slug" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Volume (oz)*</label>
                  <input required type="number" step="0.1" className="ft-input" value={form.oz} onChange={(e) => setForm({ ...form, oz: parseFloat(e.target.value) || 0 })} data-testid="bs-oz" />
                </div>
                <div>
                  <label className="ft-label">Volume (mL)*</label>
                  <input required type="number" step="1" className="ft-input" value={form.ml} onChange={(e) => setForm({ ...form, ml: parseFloat(e.target.value) || 0 })} data-testid="bs-ml" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="ft-label">Status</label>
                  <select className="ft-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="bs-status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="ft-label">Display Order</label>
                  <input type="number" className="ft-input" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: parseInt(e.target.value, 10) || 0 })} data-testid="bs-order" />
                </div>
              </div>

              <label className="flex items-center gap-3 text-sm cursor-pointer">
                <input type="checkbox" checked={form.public_visible} onChange={(e) => setForm({ ...form, public_visible: e.target.checked })} data-testid="bs-public" />
                Visible on public website
              </label>

              <div>
                <label className="ft-label">Compatible Products</label>
                <div className="text-xs text-[var(--ft-muted)] mb-3">Leave all unchecked to apply this size to every product.</div>
                <div className="space-y-2">
                  {PRODUCT_OPTIONS.map((p) => (
                    <label key={p.slug} className="flex items-center gap-3 text-sm cursor-pointer" data-testid={`bs-product-${p.slug}`}>
                      <input
                        type="checkbox"
                        checked={form.compatible_product_slugs.includes(p.slug)}
                        onChange={() => toggleProduct(p.slug)}
                      />
                      {p.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[var(--ft-border)]">
                <button type="button" onClick={() => { setEditing(null); setShowNew(false); }} className="ft-btn-ghost">Cancel</button>
                <button type="submit" className="ft-btn-primary" data-testid="bs-save">
                  {editing ? "Save Changes" : "Create Bottle Size"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBottleSizes;
